import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api } from "../api/api";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { format } from "date-fns";
import VehicleDescription from "../components/VehicleDescription";
import { FaPhone } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { Star } from 'lucide-react';
import BookingModal from "../components/BookingModal";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

function VehicleOwnerPage({ setLoading, loading }) {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState({
    fanRatings: [], // Initialize with empty arrays to prevent undefined errors
    customerRatings: [],
    imageURLs: []
  });
  const [bookedDates, setBookedDates] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  

  const getAverageRating = (ratings) => {
    if (!ratings || ratings.length === 0) return "N/A"; // Handle empty arrays
    const sum = ratings.reduce((acc, rating) => acc + rating, 0);
    return (sum / ratings.length).toFixed(1); // Round to 1 decimal place
  };
  
  // Calculate these values after we're sure vehicle data is loaded
  const fanRatingv = getAverageRating(vehicle.fanRatings);
  const customerRatingv = getAverageRating(vehicle.customerRatings);

  const renderStars = (rating) => {
    if (rating === "N/A") return []; // Return empty array if no ratings
    
    const stars = [];
    const fullStars = Math.floor(rating);
    const partialFill = rating % 1;
    
    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        // Full star
        stars.push(
          <div key={i} className="relative inline-block mr-1">
            <Star 
              size={16}
              fill="#6F48E5" 
              stroke="#6F48E5"
            />
          </div>
        );
      } else if (i === fullStars + 1 && partialFill > 0) {
        // Partial star
        stars.push(
          <div key={i} className="relative inline-block mr-1">
            <div className="absolute top-0 left-0 overflow-hidden" style={{ width: `${partialFill * 100}%` }}>
              <Star 
                size={16}
                fill="#6F48E5" 
                stroke="#6F48E5"
              />
            </div>
            <div>
              <Star 
                size={16}
                fill="#f5f5f5" 
                stroke="#e0e0e0"
              />
            </div>
          </div>
        );
      } else {
        // Empty star
        stars.push(
          <div key={i} className="relative inline-block mr-1">
            <Star 
              size={16}
              fill="#f5f5f5" 
              stroke="#e0e0e0"
            />
          </div>
        );
      }
    }
    
    return stars;
  };

  useEffect(() => {
    
    api
      .get("/users/checklogin", { withCredentials: true })
      .then((response) => {
        setUser(response.data.user.user);
        setIsLoggedIn(true);
      })
      .catch(() => setUser(null));
      setLoading(false);
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .post("/vehicles/getvehiclebyid", { vehicleId })
      .then((response) => {
        // Make sure we have the required properties
        const vehicleData = response.data.vehicle;
        setVehicle({
          ...vehicleData,
          fanRatings: vehicleData.fanRatings || [],
          customerRatings: vehicleData.customerRatings || [],
          imageURLs: vehicleData.imageURLs || []
        });
      })
      .catch((error) => console.log(error))
      .finally(() => setLoading(false));
    
    setLoading(true);
    // Fetch booked dates
    api
      .post("/vehicles/getbookeddates", { vehicleId }) // Ensure this API exists
      .then((response) => {
        setBookedDates(response.data.bookedDates);
      })
      .catch((error) => console.log(error))
      .finally(() => setLoading(false));
  }, [vehicleId, setLoading]);

  const images = (vehicle.imageURLs || []).filter(url => url !== null);

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const tileClassName = ({ date }) => {
    const formattedDate = format(date, "yyyy-MM-dd");
    const isBooked = bookedDates.includes(formattedDate);

    return isBooked
      ? "relative pointer-events-none" // Ensure the text color stays the same
      : "pointer-events-none";
  };

  const tileContent = ({ date }) => {
    const formattedDate = format(date, "yyyy-MM-dd");
    const isBooked = bookedDates.includes(formattedDate);

    return isBooked ? (
      <span className="absolute w-2.5 h-2.5 rounded-full bg-[#6F48E5]"></span>
    ) : null;
  };

  if (loading) {
    return null;
  }

  return (
    <>
      <style>
        {`
          .react-calendar__tile--now {
            background: none !important;
            border: none !important;
            box-shadow: none !important;
          }
             .react-calendar {
            border: none !important;
          }
          
        `}
      </style>

      <div className="flex flex-row justify-center mx-5 lg:mx-10">
        <div className="relative w-full max-w-4xl overflow-hidden rounded-lg shadow-lg mt-10">
          <div className="relative w-full aspect-[16/9]">
            {images.map((img, index) => (
              <img
                key={index}
                src={img}
                className={`absolute w-full h-full object-cover transition-opacity duration-700 ${
                  index === currentIndex ? "opacity-100" : "opacity-0"
                }`}
                alt={`Slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-gray-800/50 text-white p-2 rounded-full hover:bg-gray-800/70"
          >
            &#10094;
          </button>
          <button
            onClick={nextSlide}
            className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-gray-800/50 text-white p-2 rounded-full hover:bg-gray-800/70"
          >
            &#10095;
          </button>

          {/* Indicators */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full ${
                  index === currentIndex ? "bg-white" : "bg-gray-500"
                }`}
              ></button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-row sm:justify-center sm:mt-0 -mt-15">
        <div className="flex w-full flex-col sm:flex-row justify-center lg:mx-10 lg:mt-5 sm:gap-2 lg:w-[56rem] md:w-[48rem] max-w-4xl">
          <div className="sm:w-1/2 flex rounded-lg scale-90 sm:scale-75 md:scale-90  lg:scale-100 flex-col sm:flex-row">
            <div className="bg-white p-6 shadow-2xl rounded-lg">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Booked Calendar
              </h2>
              <Calendar
                className="rounded-lg"
                tileClassName={tileClassName}
                tileContent={tileContent}
              />
            </div>
          </div>

          <div className="sm:w-1/2 bg-white shadow-2xl rounded-lg flex flex-col items-stretch scale-90 sm:scale-75 md:scale-90 lg:scale-100">
            {/* For Buttons */}
            <div className="border boder-gray w-full rounded-t-lg h-1/2 flex flex-col items-center justify-center p-6 sm:gap-0 gap-5 bg-violet-100">
              <div className="h-1/3 w-full text-justify ">
                 <b>Contact Us</b>{" "}
              </div>
              <div className="h-1/3 w-full flex flex-row justify-start space-x-2">
                <FaPhone size={20} className="text-[#6F48E5]" />
                <p>+94 78 740 6259</p>
              </div>
              <div className="h-1/3 w-full flex flex-row justify-start space-x-2">
                <MdEmail size={25} className="text-[#6F48E5]" />
                <p>hirelankapvtltd@gmail.com</p>
              </div>
            </div>
            {/* ====================================================================== */}

            <div className="w-full max-w-md  p-6 ">
              <div className="flex flex-col space-y-4">
                <div className=" flex flex-row justify-between items-center border border-gray-200 rounded-lg p-2">
                  <div>
                    <div className="text-sm font-medium text-black">Number of Clicks</div>
                  </div>
                  <span className="text-md font-bold text-black">{vehicle.numberOfClicks}</span>
                </div>
                <div className="flex flex-row justify-between items-center p-2 border border-gray-200 rounded-lg">
                  <div>
                    <h3 className="text-sm font-medium text-black">Fan Rating</h3>
                    <div className="flex flex-row mt-1">
                      {renderStars(fanRatingv)}
                    </div>
                  </div>
                  <div className="flex flex-row items-center space-x-2">
                  <span className="text-md font-medium text-black">{fanRatingv}/5.0</span><p className="text-xs text-gray-500"> ({vehicle.fanRatings.length} Votes)</p>
                  </div>
                </div>
                
                <div className="flex flex-row justify-between items-center p-2  border border-gray-200 rounded-l">
                  <div >
                    <h3 className="text-sm font-medium text-black">Customer Rating</h3>
                    <div className="flex flex-row mt-1">
                      {renderStars(customerRatingv)}
                    </div>
                  </div>
                  <div className="flex flex-row items-center space-x-2">
                  <span className="text-md font-medium text-black">{customerRatingv}/5.0</span> <p className="text-xs text-gray-500">({vehicle.customerRatings.length} Votes)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ====================================================================== */}
          </div>
        </div>
      </div>
      <div className="flex flex-row justify-center mt-5">
        <VehicleDescription vehicle={vehicle} />
      </div>
    </>
  );
}

export default VehicleOwnerPage;